import {
  TextInput,
  Grid,
  ScrollArea,
  Button,
  Select,
  LoadingOverlay,
  Tabs,
  SimpleGrid,
  Group,
} from "@mantine/core";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { showNotification } from "@mantine/notifications";
import { useForm } from "@mantine/form";
import { useViewportSize } from "@mantine/hooks";
import { CREATE_SUPPLIER_Business } from "apollo/mutuations";
import { GET_MY_SUPPLIERS_Business, GET_REGIONS } from "apollo/queries";
import { customLoader } from "components/utilities/loader";
import { MAX_FILE_SIZE } from "utiles/url";
import { IconUpload } from "@tabler/icons-react";

const SupplierBusinessAddModal = ({ setOpened, size, activePage }) => {
  const [regionsDropDownData, setRegionsDropDownData] = useState([]);
  const [isCurrentTabValid, setIsCurrentTabValid] = useState(false);

  const { loading: regionsLoading } = useQuery(GET_REGIONS, {
    variables: {
      first: 100000,
      page: 1,
    },
    onCompleted(data) {
      const regionsArray = data.regions.data.map((region) => ({
        label: region?.name,
        value: region?.id,
      }));
      setRegionsDropDownData(regionsArray);
    },
    onError(err) {
      showNotification({
        color: "red",
        title: "Error",
        message: `${err}`,
      });
    },
  });

  const form = useForm({
    initialValues: {
      business_name: "",
      business_phone_number: "",
      tin: "",
      number_of_warehouses: null,
      business_region_id: "",
      business_woreda: "",
    },
    validate: {
      business_name: (value) => (!value.trim() ? "Required" : null),
      business_phone_number: (value) =>
        !value.trim()
          ? "Required"
          : !/^\+?\d+$/.test(value)
          ? "Invalid phone number"
          : null,
      number_of_warehouses: (value) => (!value ? "Required" : null),
      tin: (value) => {
        if (!value.trim()) return "Required";
        if (value.length > 10) return "TIN must be maximum 10 digits";
        if (!/^\d+$/.test(value)) return "TIN must contain only digits";
        return null;
      },
      business_region_id: (value) => (!value ? "Region is required" : null),
      business_woreda: (value) => (!value.trim() ? "Woreda is required" : null),
    },
  });

  const [fileErrors, setFileErrors] = useState({
    owner_id: "",
    tin_certification_document: "",
  });

  const [files, setFiles] = useState({
    owner_id: [],
    tin_certification_document: [],
  });

  const fileInputRefs = {
    owner_id: useRef(null),
    tin_certification_document: useRef(null),
  };

  const validateCurrentTab = () => {
    let valid = true;

    switch (activeTab) {
      case "business-info":
        const fields = [
          "business_name",
          "business_phone_number",
          "number_of_warehouses",
          "tin",
          "business_region_id",
          "business_woreda",
        ];
        fields.forEach((field) => {
          if (form.validateField(field).error) valid = false;
        });
        break;

      case "business-documents":
        if (files.owner_id.length === 0) {
          setFileErrors((prev) => ({ ...prev, owner_id: "Required" }));
          valid = false;
        } else {
          setFileErrors((prev) => ({ ...prev, owner_id: "" }));
        }
        if (files.tin_certification_document.length === 0) {
          setFileErrors((prev) => ({
            ...prev,
            tin_certification_document: "Required",
          }));
          valid = false;
        } else {
          setFileErrors((prev) => ({
            ...prev,
            tin_certification_document: "",
          }));
        }
        break;

      default:
        break;
    }

    return valid;
  };

  const [createSupplierBusiness, { loading: supplierLoading }] = useMutation(
    CREATE_SUPPLIER_Business,
    {
      update(cache, { data: { createSupplierBusiness } }) {
        cache.updateQuery(
          {
            query: GET_MY_SUPPLIERS_Business,
            variables: {
              first: parseInt(size),
              page: activePage,
              search: "",
              ordered_by: [{ column: "CREATED_AT", order: "DESC" }],
            },
          },
          (data) => {
            if (data) {
              return {
                myBusinesses: {
                  ...data.myBusinesses,
                  data: [createSupplierBusiness, ...data.myBusinesses.data],
                },
              };
            }
          }
        );
      },
    }
  );

  const { height } = useViewportSize();

  const submit = () => {
    const variables = {
      business_name: form.values.business_name,
      business_phone_number: form.values.business_phone_number,
      tin: form.values.tin,
      number_of_warehouses: parseInt(form.values.number_of_warehouses),
      business_region_id: form.values.business_region_id,
      business_woreda: form.values.business_woreda,
      owner_id: files.owner_id[0],
      tin_certification_document: files.tin_certification_document[0],
      is_agreed: true,
    };

    createSupplierBusiness({
      variables,
      onCompleted() {
        showNotification({
          color: "green",
          title: "Success",
          message: "Supplier Business Created Successfully",
        });
        setOpened(false);
      },
      onError(error) {
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message || "Failed to Create Business",
        });
      },
    });
  };

  const handleFileChange = (event, fileType) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    let hasError = false;

    selectedFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        setFileErrors((prev) => ({
          ...prev,
          [fileType]: `File size exceeds 1 MB limit`,
        }));
        showNotification({
          color: "red",
          title: "Error",
          message: `${file.name} exceeds 1 MB. Please upload a smaller file.`,
        });
        hasError = true;
      } else {
        validFiles.push(file);
        setFileErrors((prev) => ({ ...prev, [fileType]: "" }));
      }
    });

    if (!hasError) {
      setFiles((prev) => ({
        ...prev,
        [fileType]: validFiles,
      }));
    }
  };

  const FilePreview = ({ fileList }) => {
    const previews = useMemo(() => {
      return fileList.map((file, index) => {
        const fileUrl = URL.createObjectURL(file);
        return (
          <div key={index} style={{ margin: "0 auto" }}>
            {file.type === "application/pdf" ? (
              <embed
                src={fileUrl}
                width="130"
                height="160"
                type="application/pdf"
                onLoad={() => URL.revokeObjectURL(fileUrl)}
              />
            ) : (
              <img
                src={fileUrl}
                alt="preview"
                width="130"
                onLoad={() => URL.revokeObjectURL(fileUrl)}
              />
            )}
          </div>
        );
      });
    }, [fileList]);

    return (
      <SimpleGrid
        cols={3}
        spacing="md"
        style={{ maxHeight: "200px", overflowY: "auto" }}
      >
        {previews}
      </SimpleGrid>
    );
  };

  const [activeTab, setActiveTab] = useState("business-info");
  const tabList = ["business-info", "business-documents"];

  const nextTab = () => {
    if (!validateCurrentTab()) return;
    const currentIndex = tabList.indexOf(activeTab);
    if (currentIndex < tabList.length - 1) {
      setActiveTab(tabList[currentIndex + 1]);
    }
  };

  const prevTab = () => {
    const currentIndex = tabList.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabList[currentIndex - 1]);
    }
  };

  const setRegionDropDownValue = (val) => {
    form.setFieldValue("business_region_id", val);
  };

  useEffect(() => {
    setIsCurrentTabValid(validateCurrentTab());
  }, [activeTab, form.values, files]);

  return (
    <>
      <LoadingOverlay
        visible={supplierLoading}
        color="blue"
        overlayBlur={2}
        loader={customLoader}
      />
      <ScrollArea style={{ height: height / 1.8 }} type="auto" offsetScrollbars>
        <form onSubmit={form.onSubmit(submit)} noValidate>
          <Tabs
            value={activeTab}
            onTabChange={setActiveTab}
            styles={{
              tabList: { borderBottom: `2px solid #FF6A00` },
              tab: {
                "&[data-active]": { borderColor: "#FF6A00" },
                "&:hover": { borderColor: "#FF6A00" },
              },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="business-info">Business Information</Tabs.Tab>
              <Tabs.Tab value="business-documents">Business Documents</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="business-info">
              <Grid gutter="md">
                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="Business Name"
                    placeholder="Business Name"
                    {...form.getInputProps("business_name")}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="Business Phone Number"
                    placeholder="Business Phone Number"
                    {...form.getInputProps("business_phone_number")}
                    maxLength={13}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput
                    required
                    type="number"
                    label="Number of Warehouses"
                    placeholder="Number of Warehouses"
                    {...form.getInputProps("number_of_warehouses")}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="TIN"
                    placeholder="Tax Identification Number"
                    maxLength={10}
                    {...form.getInputProps("tin")}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <Select
                    required
                    searchable
                    label="Region"
                    placeholder="Pick a region"
                    data={regionsDropDownData}
                    value={form.values.business_region_id}
                    onChange={setRegionDropDownValue}
                    error={form.errors.business_region_id}
                  />
                </Grid.Col>
                <Grid.Col span={4}>
                  <TextInput
                    required
                    label="Woreda"
                    placeholder="Woreda"
                    {...form.getInputProps("business_woreda")}
                  />
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="business-documents">
              <Grid>
                <Grid.Col span={6}>
                  <div style={{ marginTop: 20 }}>
                    <Group align="flex-start" spacing="md" wrap="nowrap">
                      <div style={{ width: 160 }}>
                        <Button
                          fullWidth
                          onClick={() => fileInputRefs.owner_id.current.click()}
                          variant="outline"
                          leftIcon={<IconUpload size={16} />}
                        >
                          Owner ID
                        </Button>

                        {fileErrors.owner_id && (
                          <div
                            style={{ color: "red", fontSize: 12, marginTop: 4 }}
                          >
                            {fileErrors.owner_id}
                          </div>
                        )}

                        <input
                          type="file"
                          ref={fileInputRefs.owner_id}
                          accept="image/png, image/jpeg, image/jpg,.pdf"
                          style={{ display: "none" }}
                          onChange={(e) => handleFileChange(e, "owner_id")}
                        />
                      </div>

                      <FilePreview fileList={files.owner_id} />
                    </Group>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <Group align="flex-start" spacing="md" wrap="nowrap">
                      <div style={{ width: 160 }}>
                        <Button
                          fullWidth
                          onClick={() =>
                            fileInputRefs.tin_certification_document.current.click()
                          }
                          variant="outline"
                          leftIcon={<IconUpload size={16} />}
                        >
                          TIN Certificate
                        </Button>

                        {fileErrors.tin_certification_document && (
                          <div
                            style={{ color: "red", fontSize: 12, marginTop: 4 }}
                          >
                            {fileErrors.tin_certification_document}
                          </div>
                        )}

                        <input
                          type="file"
                          ref={fileInputRefs.tin_certification_document}
                          accept="image/png, image/jpeg, image/jpg,.pdf"
                          style={{ display: "none" }}
                          onChange={(e) =>
                            handleFileChange(e, "tin_certification_document")
                          }
                        />
                      </div>
                      <FilePreview
                        fileList={files.tin_certification_document}
                      />
                    </Group>
                  </div>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>
          </Tabs>

          <Grid mt="xl">
            <Grid.Col span={12}>
              <Button
                onClick={prevTab}
                style={{ backgroundColor: "#FF6A00", color: "#FFFFFF" }}
                disabled={activeTab === "business-info"}
              >
                Previous
              </Button>

              {activeTab === "business-documents" ? (
                <Button
                  type="submit"
                  style={{
                    marginLeft: 10,
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  disabled={
                    !files.owner_id.length ||
                    !files.tin_certification_document.length
                  }
                >
                  Submit
                </Button>
              ) : (
                <Button
                  onClick={nextTab}
                  style={{
                    marginLeft: 14,
                    backgroundColor: "#FF6A00",
                    color: "#FFFFFF",
                  }}
                  disabled={!isCurrentTabValid}
                >
                  Next
                </Button>
              )}
            </Grid.Col>
          </Grid>
        </form>
      </ScrollArea>
    </>
  );
};

export default SupplierBusinessAddModal;
